import { ChevronRight, Plus, Route } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatRail } from "@/shared/utils/railDisplay";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import type { CorridorRouteCell } from "../../types/paymentRoutes.types";
import {
  ROUTE_STATUS_CONSEQUENCE_KEY,
  ROUTE_STATUS_LABEL_KEY,
  ROUTE_STATUS_PILL_VARIANT,
} from "../../utils/routeFormat";

export interface CorridorRouteCardsProps {
  cells: CorridorRouteCell[];
  isLoading: boolean;
  onView: (cell: CorridorRouteCell) => void;
  onCreate: (cell: CorridorRouteCell) => void;
}

/**
 * Mobile rendering of the coverage rows: one card per corridor and direction, never a squeezed table.
 *
 * The consequence line is inline here rather than behind the status tooltip a table can afford — on touch
 * there is no hover, and "no route" without "refuses every operation" next to it is not actionable.
 */
export function CorridorRouteCards({ cells, isLoading, onView, onCreate }: CorridorRouteCardsProps) {
  const { t } = useTranslations("paymentRoutes");
  const labels = useFeeLabels();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="h-36 animate-pulse rounded-xl border border-border bg-muted" />
        ))}
      </div>
    );
  }

  if (cells.length === 0) {
    return <ColocaEmpty icon={Route} title={t("empty_title")} description={t("empty_description")} />;
  }

  return (
    <div className="flex flex-col gap-3">
      {cells.map((cell) => (
        <div
          key={cell.key}
          className={cn(
            "rounded-xl border border-border bg-card p-4",
            cell.status === "MISSING" && "border-l-2 border-l-destructive",
            cell.offCatalog && "bg-muted/40",
          )}
        >
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            <ColocaBadge variant="default">{cell.country}</ColocaBadge>
            <ColocaBadge variant="secondary" className="font-sans">
              {formatRail(cell.rail)}
            </ColocaBadge>
            <span className="font-mono text-xs text-muted-foreground">{cell.currency}</span>
            <ColocaBadge variant="default" className="ml-auto font-sans">
              {labels.direction[cell.direction]}
            </ColocaBadge>
          </div>

          {cell.offCatalog && (
            <ColocaBadge variant="secondary" className="mb-2 border-dashed font-sans text-muted-foreground">
              {t("offcatalog_badge")}
            </ColocaBadge>
          )}

          <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm">
            <dt className="text-muted-foreground">{t("th_model")}</dt>
            <dd className="text-right text-xs text-muted-foreground">
              {cell.route ? labels.model[cell.route.economic_model] : "—"}
            </dd>

            <dt className="text-muted-foreground">{t("th_provider")}</dt>
            <dd
              className={cn(
                "text-right text-xs",
                cell.route && !cell.providerDeclaresCorridor ? "text-warning" : "text-muted-foreground",
              )}
            >
              {!cell.route
                ? t("provider_empty")
                : cell.providerDeclaresCorridor
                  ? t("provider_corridors", { count: cell.providerCorridorCount })
                  : t("provider_unknown")}
            </dd>
          </dl>

          <p className="mt-3 text-xs text-muted-foreground">{t(ROUTE_STATUS_CONSEQUENCE_KEY[cell.status])}</p>

          <div className="mt-3 flex items-center gap-2">
            <ColocaStatusPill variant={ROUTE_STATUS_PILL_VARIANT[cell.status]} size="sm">
              {t(ROUTE_STATUS_LABEL_KEY[cell.status])}
            </ColocaStatusPill>
            {cell.route ? (
              <ColocaButton
                variant="ghost"
                size="sm"
                className="ml-auto"
                iconTrailing={ChevronRight}
                onClick={() => onView(cell)}
              >
                {t("action_view")}
              </ColocaButton>
            ) : (
              <ColocaButton
                variant="ghost"
                size="sm"
                className="ml-auto text-warning"
                icon={Plus}
                onClick={() => onCreate(cell)}
              >
                {t("action_create")}
              </ColocaButton>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
